import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Users, AlertTriangle, UserPlus, Pencil, CheckCircle2, ChevronLeft } from "lucide-react";
import { toast } from "react-toastify";
import { useAppSelector } from "../../../store/hooks";
import conferenceAndTradeMissionService from "../../../api/ConferenceAndTradeMission";
import generalService from "../../../api/GeneralService";
import { normalizeContactNumber, validateContactNumber } from "../../../utils/formValidators";
import RegistrationLayout from "./RegistrationLayout";
import StepAgentReferral from "./StepAgentReferral";
import Step1GroupInfo from "./Step1GroupInfo";
import AttendeeDrawer from "./AttendeeDrawer";
import StepPaymentChoice from "./StepPaymentChoice";

const GENERIC_PROFILE_PHOTO_BASE64 =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAusB9WlH0xkAAAAASUVORK5CYII=";

export default function GroupConferenceRegistration({ onBackToTypeSelection }) {
  const navigate = useNavigate();
  const { slug } = useParams();
  const location = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [referralFormData, setReferralFormData] = useState({
    hasReferralCode: null,
    agentReferralCode: "",
    validatedReferralCode: false,
    agentDetails: null,
  });
  const [referralValidationState, setReferralValidationState] = useState({ isValidating: false, isValid: false });
  const [groupFormData, setGroupFormData] = useState({
    organizationName: "",
    groupCountry: "",
    groupCity: "",
    numberOfAttendees: "",
    primaryContactName: "",
    primaryContactPhone: "",
    primaryContactEmail: "",
    primaryContactSignature: null,
  });
  const [attendees, setAttendees] = useState([]);
  const [drawerIndex, setDrawerIndex] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [showPaymentChoice, setShowPaymentChoice] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  const numAttendees = Math.max(0, parseInt(groupFormData.numberOfAttendees, 10) || 0);
  const canProceedFromAttendees = attendees.length === numAttendees && numAttendees >= 2;
  const totalAmount = Number((numAttendees * (parseFloat(selectedTicket?.price) || 0)).toFixed(2));
  const isFreeTicket = totalAmount === 0;

  // Never show Pay now / Pay later for free tickets: redirect to success if we ever would
  useEffect(() => {
    if (showPaymentChoice && isFreeTicket && registrationData) {
      navigate(`/event-details/${slug}/registration-success`, {
        state: {
          applicationId: slug,
          registrationId: registrationData?.id || registrationData?.registration_id,
          registrationData,
          paymentVerified: true,
          isFreeTicket: true,
          isGroup: true,
        },
      });
    }
  }, [showPaymentChoice, isFreeTicket, registrationData, navigate, slug]);

  useEffect(() => {
    let ticketFromState = location.state?.selectedTicket || null;
    if (!ticketFromState && slug) {
      try {
        const stored = sessionStorage.getItem(`conferenceRegistrationTicket_${slug}`);
        if (stored) ticketFromState = JSON.parse(stored);
      } catch {}
    }
    if (!ticketFromState) {
      toast.info("Please select a ticket category to continue");
      navigate(`/event-details/${slug}`);
      return;
    }
    setSelectedTicket(ticketFromState);
  }, [location.state, navigate, slug]);

  useEffect(() => {
    if (!isAuthenticated) {
      toast.info("Please sign in to register for this event");
      navigate("/signin", { state: { from: `/event-details/${slug}/register` } });
    }
  }, [isAuthenticated, navigate, slug]);

  useEffect(() => {
    const fetchEventData = async () => {
      if (!slug) return;
      setLoading(true);
      try {
        const response = await conferenceAndTradeMissionService.getEventDetails(slug);
        if (response.data?.status_code === 200 && response.data?.data?.event) {
          setEventData(response.data.data.event);
        } else {
          toast.error("Failed to load event details");
          navigate(`/event-details/${slug}`);
        }
      } catch (error) {
        toast.error("An error occurred while loading event details");
        navigate(`/event-details/${slug}`);
      } finally {
        setLoading(false);
      }
    };
    fetchEventData();
  }, [slug, navigate]);

  const validateGroupStep = () => {
    const newErrors = {};
    if (!groupFormData.organizationName?.trim()) newErrors.organizationName = "Organization name is required";
    if (!groupFormData.groupCountry?.trim()) newErrors.groupCountry = "Country is required";
    if (!groupFormData.groupCity?.trim()) newErrors.groupCity = "City is required";
    if (!groupFormData.primaryContactName?.trim()) newErrors.primaryContactName = "Primary contact name is required";
    if (!groupFormData.primaryContactPhone?.trim()) {
      newErrors.primaryContactPhone = "Primary contact phone is required";
    } else {
      const phoneValidation = validateContactNumber(groupFormData.primaryContactPhone);
      if (!phoneValidation.isValid) {
        newErrors.primaryContactPhone = phoneValidation.message;
      }
    }
    if (!groupFormData.primaryContactEmail?.trim()) {
      newErrors.primaryContactEmail = "Primary contact email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(groupFormData.primaryContactEmail)) {
      newErrors.primaryContactEmail = "Please enter a valid email address";
    }
    if (!groupFormData.primaryContactSignature) newErrors.primaryContactSignature = "Primary contact signature is required";
    const n = parseInt(groupFormData.numberOfAttendees, 10);
    if (!n || n < 2) newErrors.numberOfAttendees = "Number of attendees must be at least 2";
    if (n > 50) newErrors.numberOfAttendees = "Maximum 50 attendees";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAgentReferral = async (code) => {
    try {
      const response = await generalService.validateAgentReferralCode(code);
      if (response.data?.status_code === 200) {
        return {
          success: true,
          message: response.data.reason || "Referral code verified successfully!",
          agentDetails: response.data.data?.agent,
        };
      }
      return {
        success: false,
        message: response.data?.reason || "Invalid referral code",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.data?.reason || "An error occurred while validating agent code",
      };
    }
  };

  const validateReferralStep = () => {
    const newErrors = {};
    if (referralFormData.hasReferralCode === null) {
      newErrors.hasReferralCode = "Please indicate if you have a referral code";
    } else if (referralFormData.hasReferralCode === true) {
      if (!referralFormData.agentReferralCode?.trim()) {
        newErrors.agentReferralCode = "Please enter your referral code";
      } else if (!referralFormData.validatedReferralCode) {
        newErrors.agentReferralCode = "Please verify your referral code before proceeding";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextFromAgentStep = () => {
    if (!validateReferralStep()) {
      toast.error("Please complete the referral step before continuing");
      return;
    }
    setCurrentStep(1);
    setErrors({});
  };

  const getApiErrorMessage = (dataOrError, fallback) => {
    const data = dataOrError?.data ?? dataOrError?.response?.data;
    if (!data) return dataOrError?.message || fallback;
    // Prefer explicit reason/message fields
    if (data.reason && typeof data.reason === "string") return data.reason;
    if (data.message && typeof data.message === "string") return data.message;
    // Some APIs wrap the payload under `data`, e.g. { data: { status_code, message, reason } }
    if (data.data && typeof data.data === "object") {
      if (typeof data.data.reason === "string" && data.data.reason) return data.data.reason;
      if (typeof data.data.message === "string" && data.data.message) return data.data.message;
    }
    if (data.errors && typeof data.errors === "object") {
      const firstKey = Object.keys(data.errors)[0];
      const firstMessages = data.errors[firstKey];
      if (Array.isArray(firstMessages) && firstMessages[0]) return firstMessages[0];
      if (typeof firstMessages === "string") return firstMessages;
    }
    return fallback;
  };

  const fileToBase64 = (file) => {
    if (!file) return Promise.resolve(null);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  const handleNextGroupStep = () => {
    if (!validateGroupStep()) {
      toast.error("Please fill all required group fields");
      return;
    }
    setCurrentStep(2);
    setErrors({});
  };

  const handleAddAttendee = () => {
    setDrawerIndex(attendees.length);
  };

  const handleEditAttendee = (index) => {
    setDrawerIndex(index);
  };

  const handleDrawerSave = (data) => {
    setAttendees((prev) => {
      const next = [...prev];
      if (drawerIndex !== null && drawerIndex < prev.length) {
        next[drawerIndex] = data;
      } else {
        next.push(data);
      }
      return next;
    });
    setDrawerIndex(null);
  };

  const handleDrawerSaveAndAddNext = (data) => {
    setAttendees((prev) => {
      const next = [...prev, data];
      queueMicrotask(() => setDrawerIndex(next.length));
      return next;
    });
  };

  const handleSubmitGroupRegistration = async () => {
    setIsSubmitting(true);
    try {
      const clientSlug = user?.slug || user?.client_slug;
      if (!clientSlug) {
        toast.error("Your account session is missing. Please sign in again.");
        setIsSubmitting(false);
        return;
      }
      const ticketPrice = parseFloat(selectedTicket?.price) || 0;
      const totalAmount = Number((numAttendees * ticketPrice).toFixed(2));
      const attendeePayloads = attendees.map((a) => ({
        first_name: a.firstName,
        last_name: a.lastName,
        nationality: a.nationality,
        email: a.email,
        gender: a.gender || null,
        date_of_birth: a.dateOfBirth || null,
        profession: a.profession || null,
        organization: a.organization || null,
        country_of_residence: a.countryOfResidence || null,
        departure_city: a.departureCity || null,
        id_card_front: a.idCardFront || null,
        address: a.address || null,
        contact_number: normalizeContactNumber(a.contactNumber) || null,
        motivation_answers: Array.isArray(a.motivation_answers) ? a.motivation_answers : [],
      }));
      // Build generic motivation_answers for primary contact so API receives expected shape (per-attendee answers are in attendees[])
      const mq = eventData?.motivational_questions || [];
      const genericAnswer = "N/A (group registration – see individual attendee responses)";
      let primaryMotivationAnswers = [];
      if (mq.length > 0) {
        const isNewFormat = mq[0]?.sectionTitle !== undefined || mq[0]?.section_title !== undefined;
        if (isNewFormat) {
          mq.forEach((section) => {
            section.questions?.forEach((q) => {
              primaryMotivationAnswers.push({
                section: section.sectionTitle ?? section.section_title ?? "",
                question: q.question,
                answer: genericAnswer,
                type: q.type,
              });
            });
          });
        } else {
          primaryMotivationAnswers = mq.map((q) => ({ question: q.question, answer: genericAnswer }));
        }
      }
      const primary_contact_details = {
        primary_contact_name: groupFormData.primaryContactName || null,
        primary_contact_phone: normalizeContactNumber(groupFormData.primaryContactPhone) || null,
        primary_contact_email: groupFormData.primaryContactEmail || null,
        profile_photo: GENERIC_PROFILE_PHOTO_BASE64,
        motivation_answers: primaryMotivationAnswers,
      };
      let signatureBase64 = null;
      if (groupFormData.primaryContactSignature instanceof File) {
        signatureBase64 = await fileToBase64(groupFormData.primaryContactSignature);
      } else if (typeof groupFormData.primaryContactSignature === "string" && groupFormData.primaryContactSignature.startsWith("data:")) {
        signatureBase64 = groupFormData.primaryContactSignature;
      }
      const payload = {
        client_slug: clientSlug,
        event_slug: slug,
        event_type: "conference",
        registration_type: "group",
        // Mirror backend expectations used during payment validation
        number_of_attendees: numAttendees,
        ticket_type: selectedTicket,
        group_info: {
          organization_name: groupFormData.organizationName,
          country: groupFormData.groupCountry,
          city: groupFormData.groupCity,
          number_of_attendees: numAttendees,
          primary_contact_name: groupFormData.primaryContactName || null,
          primary_contact_phone: normalizeContactNumber(groupFormData.primaryContactPhone) || null,
          primary_contact_email: groupFormData.primaryContactEmail || null,
          primary_contact_signature: signatureBase64,
        },
        primary_contact_details,
        attendees: attendeePayloads,
        total_amount: Math.round(totalAmount),
      };
      if (referralFormData.hasReferralCode && referralFormData.agentReferralCode && referralFormData.validatedReferralCode) {
        payload.agent_code = referralFormData.agentReferralCode;
      }
      const response = await conferenceAndTradeMissionService.registerTradeMission(payload);
      if (response.data?.status_code === 200) {
        const registrationInfo = response.data.data;
        setRegistrationData(registrationInfo);
        // Free/0 amount ticket: skip payment, go straight to success
        const isFreeTicket = ticketPrice === 0;
        if (isFreeTicket) {
          toast.success(response.data.reason || "Group registration completed!");
          navigate(`/event-details/${slug}/registration-success`, {
            state: {
              applicationId: slug,
              registrationId: registrationInfo?.id || registrationInfo?.registration_id,
              registrationData: registrationInfo,
              paymentVerified: true,
              isFreeTicket: true,
              isGroup: true,
            },
          });
        } else {
          toast.success(response.data.reason || "Group registration submitted!");
          setShowPaymentChoice(true);
          setCurrentStep(3);
        }
      } else {
        toast.error(getApiErrorMessage(response, "Failed to submit group registration."));
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "An error occurred. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayLater = () => {
    const total = Number((numAttendees * (parseFloat(selectedTicket?.price) || 0)).toFixed(2));
    navigate(`/event-details/${slug}/registration-success`, {
      state: {
        applicationId: slug,
        registrationId: registrationData?.id || registrationData?.registration_id,
        registrationData,
        eventData,
        paymentVerified: false,
        payLater: true,
        isGroup: true,
        selectedTicket,
        numberOfAttendees: numAttendees,
        totalAmount: total,
        attendees,
      },
    });
  };

  const handlePayNow = () => {
    navigate(`/event-details/${slug}/payment`, {
      state: {
        registrationData,
        eventData,
        slug,
        selectedTicket,
        isGroup: true,
        numberOfAttendees: numAttendees,
        totalAmount: Number((numAttendees * (parseFloat(selectedTicket?.price) || 0)).toFixed(2)),
        attendees,
      },
    });
  };

  const handleBack = () => {
    if (currentStep === 0) {
      if (onBackToTypeSelection) onBackToTypeSelection();
      else setShowExitModal(true);
    } else if (currentStep === 1) {
      setCurrentStep(0);
      setErrors({});
    } else if (currentStep === 2) {
      setCurrentStep(1);
      setErrors({});
    } else if (currentStep === 3) {
      setCurrentStep(2);
      setErrors({});
    } else if (showPaymentChoice) {
      setShowPaymentChoice(false);
      setCurrentStep(2);
    }
  };

  const handleClose = () => {
    if (currentStep > 0 || showPaymentChoice) setShowExitModal(true);
    else navigate(`/event-details/${slug}`);
  };

  const renderStep = () => {
    if (showPaymentChoice && !isFreeTicket) {
      return (
        <StepPaymentChoice
          formData={{ ...groupFormData, selectedTicket, numberOfAttendees: numAttendees, totalAmount }}
          setFormData={() => {}}
          onPayNow={handlePayNow}
          onPayLater={handlePayLater}
          isGroup
          totalAmount={totalAmount}
          numberOfAttendees={numAttendees}
        />
      );
    }
    if (showPaymentChoice && isFreeTicket) {
      return (
        <div className="flex flex-col justify-center items-center py-12 text-center">
          <p className="text-gray-600">Redirecting to confirmation...</p>
        </div>
      );
    }
    if (currentStep === 0) {
      return (
        <StepAgentReferral
          formData={referralFormData}
          setFormData={setReferralFormData}
          errors={errors}
          onValidateReferral={validateAgentReferral}
          onValidationStateChange={setReferralValidationState}
        />
      );
    }
    if (currentStep === 1) {
      return (
        <Step1GroupInfo
          formData={groupFormData}
          setFormData={setGroupFormData}
          errors={errors}
        />
      );
    }
    if (currentStep === 2) {
      return (
        <motion.div
          key="attendees-step"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-6"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Users size={24} className="text-teal-600" />
              <h2 className="text-xl font-bold text-gray-900">Attendee details</h2>
            </div>
            <p className="text-sm text-gray-600">
              Add {numAttendees} attendee{numAttendees !== 1 ? "s" : ""}. First attendee is the primary contact.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {attendees.map((attendee, i) => (
              <motion.div
                key={i}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative flex flex-col rounded-xl border-2 border-gray-200 bg-gray-50/50 p-4 shadow-sm transition-colors hover:border-teal-200 hover:bg-teal-50/30"
              >
                {i === 0 && (
                  <span className="absolute top-2 right-2 rounded bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700">
                    Primary
                  </span>
                )}
                <div className="mb-2 font-semibold text-gray-900">
                  {attendee.firstName} {attendee.lastName}
                </div>
                <div className="text-sm text-gray-600">{attendee.email}</div>
                <div className="mt-2 text-xs text-gray-500">{attendee.nationality}</div>
                <button
                  type="button"
                  onClick={() => handleEditAttendee(i)}
                  className="mt-3 flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <Pencil size={14} />
                  Edit
                </button>
              </motion.div>
            ))}
            {attendees.length < numAttendees && (
              <motion.button
                type="button"
                onClick={handleAddAttendee}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex min-h-[140px] flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-teal-300 bg-teal-50/50 py-6 text-teal-700 transition-colors hover:border-teal-500 hover:bg-teal-50"
              >
                <UserPlus size={32} className="text-teal-500" />
                <span className="font-semibold">Add attendee</span>
                <span className="text-sm">
                  {attendees.length + 1} of {numAttendees}
                </span>
              </motion.button>
            )}
          </div>

          {attendees.length > 0 && attendees.length < numAttendees && (
            <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <CheckCircle2 size={18} className="shrink-0" />
              Add {numAttendees - attendees.length} more attendee{numAttendees - attendees.length !== 1 ? "s" : ""} to continue.
            </div>
          )}
        </motion.div>
      );
    }
    return null;
  };

  const steps = useMemo(
    () => [
      { number: 0, label: "Agent Referral", shortLabel: "Referral" },
      { number: 1, label: "Group Information", shortLabel: "Group Info" },
      { number: 2, label: "Attendees", shortLabel: "Attendees" },
      { number: 3, label: "Payment", shortLabel: "Payment" },
    ],
    []
  );
  const paymentStepNumber = 3;
  const displayStep = showPaymentChoice ? paymentStepNumber : currentStep;
  const handleStepClick = (stepNumber) => {
    if (showPaymentChoice) return;
    if (stepNumber <= currentStep) setCurrentStep(stepNumber);
  };

  if (loading || !selectedTicket) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-16 h-16 rounded-full border-4 border-teal-200 animate-spin border-t-teal-500" />
      </div>
    );
  }

  const getNextButtonLabel = () => {
    if (currentStep === 0) return "Continue";
    if (currentStep === 1) return "Continue to Attendees";
    if (currentStep === 2) {
      if (!canProceedFromAttendees) return `Add ${numAttendees - attendees.length} more`;
      return "Submit Group Registration";
    }
    return "";
  };

  const handleNextClick = () => {
    if (currentStep === 0) handleNextFromAgentStep();
    else if (currentStep === 1) handleNextGroupStep();
    else if (currentStep === 2) {
      if (!canProceedFromAttendees) return;
      handleSubmitGroupRegistration();
    }
  };

  return (
    <>
      <RegistrationLayout
        title="Group Registration"
        subtitle={eventData?.event_title}
        onBack={onBackToTypeSelection ?? handleClose}
        backLabel={onBackToTypeSelection ? "Back to registration type" : "Back to Event"}
        backIcon={ChevronLeft}
        onBackToEvent={onBackToTypeSelection ? handleClose : undefined}
        backToEventLabel="Back to event"
        theme="teal"
        steps={steps}
        currentStep={displayStep}
        onStepClick={handleStepClick}
        footer={
          <div className="flex justify-between items-center">
            <motion.button
              onClick={handleBack}
              disabled={currentStep === 0 && !onBackToTypeSelection}
              className={`px-6 py-3 text-sm font-semibold rounded-lg ${
                currentStep === 0 && !onBackToTypeSelection ? "text-gray-400 bg-gray-100 cursor-not-allowed" : "text-gray-700 bg-gray-100 hover:bg-gray-200"
              }`}
            >
              {currentStep === 0 && onBackToTypeSelection ? "Back to registration type" : "Back"}
            </motion.button>
            {!showPaymentChoice && (
              <motion.button
                onClick={handleNextClick}
                disabled={
                  isSubmitting ||
                  (currentStep === 0 &&
                    referralFormData.hasReferralCode === true &&
                    (!referralFormData.validatedReferralCode || referralValidationState.isValidating)) ||
                  (currentStep === 2 && !canProceedFromAttendees)
                }
                className="px-8 py-3 text-base font-bold text-white bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg shadow-md hover:from-teal-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex gap-2 items-center">
                    <span className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
                    Submitting...
                  </span>
                ) : (
                  getNextButtonLabel()
                )}
              </motion.button>
            )}
          </div>
        }
      >
        <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
      </RegistrationLayout>

      <AttendeeDrawer
        open={drawerIndex !== null}
        onClose={() => setDrawerIndex(null)}
        attendeeIndex={drawerIndex ?? 0}
        initialData={drawerIndex !== null && drawerIndex < attendees.length ? attendees[drawerIndex] : null}
        onSave={handleDrawerSave}
        onSaveAndAddNext={handleDrawerSaveAndAddNext}
        eventData={eventData}
        isLastSlot={drawerIndex !== null && drawerIndex >= numAttendees - 1}
        totalSlots={numAttendees}
        motivationalQuestions={eventData?.motivational_questions}
      />

      <AnimatePresence>
        {showExitModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex fixed inset-0 z-50 justify-center items-center backdrop-blur-sm bg-black/60"
              onClick={() => setShowExitModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="overflow-hidden fixed top-1/2 left-1/2 z-50 w-full max-w-md bg-white rounded-2xl shadow-2xl -translate-x-1/2 -translate-y-1/2"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8 pt-12">
                <div className="flex justify-center mb-6">
                  <div className="flex justify-center items-center w-20 h-20 bg-teal-100 rounded-full">
                    <AlertTriangle size={40} className="text-teal-600" />
                  </div>
                </div>
                <h3 className="mb-3 text-2xl font-bold text-center text-gray-900">Leave Registration?</h3>
                <p className="mb-8 text-center text-gray-600">Your progress will be lost. Are you sure?</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowExitModal(false)}
                    className="flex-1 px-6 py-3 text-base font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200"
                  >
                    Continue
                  </button>
                  <button
                    onClick={() => {
                      setShowExitModal(false);
                      navigate(`/event-details/${slug}`);
                    }}
                    className="flex-1 px-6 py-3 text-base font-semibold text-white bg-teal-600 rounded-xl hover:bg-teal-700"
                  >
                    Leave
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
